import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { fetchGeoHint } from "../api/geo";
import {
  localeFromCountry,
  normalizePickerLocale,
  type LocaleId,
} from "./locales";
import {
  detectBrowserLocale,
  detectInitialLocale,
  hasPersistedLocale,
  persistLocale,
  translate,
  type Vars,
} from "./runtime";
import "./catalog";

type I18nValue = {
  locale: LocaleId;
  setLocale: (locale: LocaleId) => void;
  t: (key: string, vars?: Vars) => string;
};

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<LocaleId>(() => {
    const initial = detectInitialLocale();
    if (hasPersistedLocale()) persistLocale(initial);
    return initial;
  });

  const setLocale = useCallback((next: LocaleId) => {
    const normalized = normalizePickerLocale(next);
    setLocaleState(normalized);
    persistLocale(normalized);
  }, []);

  // First visit: geo country → locale, then browser, then keep en.
  useEffect(() => {
    if (hasPersistedLocale()) return;
    let cancelled = false;
    void (async () => {
      const hint = await fetchGeoHint();
      if (cancelled) return;
      const fromGeo = localeFromCountry(hint.country);
      const next = fromGeo
        ? normalizePickerLocale(fromGeo)
        : detectBrowserLocale() ?? "en";
      setLocaleState(next);
      persistLocale(next);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const t = useCallback(
    (key: string, vars?: Vars) => translate(locale, key, vars),
    [locale]
  );

  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}

export function useT() {
  return useI18n().t;
}

export function useLocale() {
  const { locale, setLocale } = useI18n();
  return { locale, setLocale };
}
